import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

// Import posts
function importPosts() {
  const context = require.context("../blog-posts", false, /\.json$/);
  return context.keys().map((key) => ({
    ...(context(key)),
    slug: key.replace(/^\.\//, "").replace(/\.json$/, ""),
  }));
}
const allPosts = importPosts().sort((a, b) => new Date(b.date) - new Date(a.date));
const MAX_POSTS = 10;
const latestPosts = allPosts.slice(0, MAX_POSTS);

// Breakpoints for # of cards shown
function getVisibleCards(width) {
  if (width <= 825) return 1;
  if (width <= 1180) return 2;
  return 3;
}

// Card width in px
const CARD_WIDTH = 300;
const CARD_GAP = 24; // px; gap-6 in Tailwind is 1.5rem = 24px

export default function LatestPostsCarousel() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [visible, setVisible] = useState(getVisibleCards(window.innerWidth));
  const [idx, setIdx] = useState(getVisibleCards(window.innerWidth));
  const [transition, setTransition] = useState(true);
  const [paused, setPaused] = useState(false);

  const postsCount = latestPosts.length;
  const trackRef = useRef();

  // Responsive cards per view
  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      setWindowWidth(w);
      setVisible(getVisibleCards(w));
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Infinite carousel logic (clone ends)
  const posts = [
    ...latestPosts.slice(-visible),
    ...latestPosts,
    ...latestPosts.slice(0, visible),
  ];

  // Keep idx in sync with visible
  useEffect(() => {
    setIdx(visible);
    setTransition(false);
    setTimeout(() => setTransition(true), 50);
    // eslint-disable-next-line
  }, [visible, postsCount]);

  // Infinite loop edge handling
  useEffect(() => {
    if (!transition) return;
    if (idx === 0) {
      setTimeout(() => {
        setTransition(false);
        setIdx(postsCount);
      }, 400);
    } else if (idx === postsCount + visible) {
      setTimeout(() => {
        setTransition(false);
        setIdx(visible);
      }, 400);
    }
  }, [idx, postsCount, visible, transition]);

  // After snap, re-enable transition
  useEffect(() => {
    if (!transition) setTimeout(() => setTransition(true), 15);
  }, [transition]);

  // Auto-move carousel
  useEffect(() => {
    if (!paused && postsCount > visible) {
      const timer = setInterval(() => setIdx((i) => i + 1), 4000);
      return () => clearInterval(timer);
    }
  }, [paused, postsCount, visible]);

  // Touch swipe
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let startX = null;
    function onTouchStart(e) { startX = e.touches[0].clientX; }
    function onTouchEnd(e) {
      if (startX === null) return;
      const endX = e.changedTouches[0].clientX;
      if (endX - startX > 40) handlePrev();
      else if (startX - endX > 40) handleNext();
      startX = null;
    }
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  });

  function handlePrev() {
    if (postsCount > visible) setIdx((i) => i - 1);
  }
  function handleNext() {
    if (postsCount > visible) setIdx((i) => i + 1);
  }

  // The width of the whole sliding track container: card widths + gaps
  const carouselWidth = visible * CARD_WIDTH + (visible - 1) * CARD_GAP;

  return (
    <div
      className="max-w-6xl px-6 py-8 bg-[var(--bg-page)] rounded-lg border border-[var(--bg-surface)] shadow-lg select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <h2 className="text-2xl font-bold text-center mb-2 text-[var(--blue)]">
        Read The Latest Gaming News
      </h2>
      <p className="text-center text-sm text-[var(--secondary-text)] max-w-2xl mx-auto mb-8">
        Stay updated with the latest League of Legends news, esports highlights and more!
      </p>
      <div className="relative flex items-center justify-center min-h-[320px]">
        {/* Prev button, OUTSIDE */}
        <div className="flex items-center justify-center h-full pr-1 sm:pr-4">
          <button
            onClick={handlePrev}
            className="z-10 p-2 rounded-full bg-[var(--bg-surface-variant)] text-2xl text-white hover:bg-[var(--blue)] transition disabled:opacity-30"
            aria-label="Previous posts"
            tabIndex={0}
            disabled={postsCount <= visible}
            style={{
              position: "relative",
              left: 0,
              boxShadow: "0 0 0 2px rgba(0,0,0,0.06)",
            }}
          >
            ‹
          </button>
        </div>
        {/* Sliding carousel track */}
        <div
          className="overflow-hidden"
          style={{
            width: `${carouselWidth}px`,
            margin: "0 auto",
          }}
        >
          <div
            ref={trackRef}
            className="flex"
            style={{
              gap: `${CARD_GAP}px`,
              width: `${(posts.length * CARD_WIDTH + (posts.length - 1) * CARD_GAP)}px`,
              transition: transition
                ? "transform 0.5s cubic-bezier(0.45, 0, 0.55, 1)"
                : "none",
              transform: `translateX(-${idx * (CARD_WIDTH + CARD_GAP)}px)`,
            }}
          >
            {posts.map((post, i) => (
              <Link
                key={post.slug + i}
                to={`/blog/${post.slug}`}
                style={{
                  width: `${CARD_WIDTH}px`,
                  flex: "0 0 auto",
                }}
                tabIndex={0}
              >
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg overflow-hidden min-h-[320px] flex flex-col shadow-md hover:shadow-xl transition">
                  <div className="w-full aspect-video overflow-hidden min-h-[160px]">
                    <img
                      src={require(`../assets/Blog/${post.image}`)}
                      alt={post.imageAlt || post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <h3
                      className="text-lg font-semibold text-white mb-2"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {post.title}
                    </h3>
                    <time className="text-xs text-[var(--secondary-text)]">
                      {new Date(post.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                    <p
                      className="text-sm text-[var(--secondary-text)] mt-2"
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {post.summary || "No excerpt available."}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        {/* Next button, OUTSIDE */}
        <div className="flex items-center justify-center h-full pl-1 sm:pl-4">
          <button
            onClick={handleNext}
            className="z-10 p-2 rounded-full bg-[var(--bg-surface-variant)] text-2xl text-white hover:bg-[var(--blue)] transition disabled:opacity-30"
            aria-label="Next posts"
            tabIndex={0}
            disabled={postsCount <= visible}
            style={{
              position: "relative",
              right: 0,
              boxShadow: "0 0 0 2px rgba(0,0,0,0.06)",
            }}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
